import _ from 'lodash'

import editorServerFacade from '../../editorServerFacade/editorServerFacade'
import constants from '../../platform/common/constants'
import type {
    AppDefinitionId,
    ClientSpecMap,
    ClientSpecMapEntry,
    EditorClientSpecMapEntry
} from '@wix/document-services-types'

import installedTpaAppsOnSiteService from './installedTpaAppsOnSiteService'
import pendingAppsService from './pendingAppsService'

export interface App {
    appDefinitionId: AppDefinitionId
    sourceTemplateId?: string
    version?: string
    appVersion?: string
    applicationId?: string | number
    instanceId?: string
    appFields?: {
        installedVersion?: string
    }
    overrideId?: string
}

export interface AppAction {
    type?: string
    appDefId?: string
    applicationId?: string | number
    instanceId?: string
    appVersion?: string
    version?: string
    sourceTemplateId?: string
    legacyParams?: {
        sourceTemplateId: string
    }
    devVersion?: {
        overrideId?: string
    }
}

export interface AppsStateDelta {
    newlyAddedApps: AppAction[]
    newlyRemovedApps: string[]
}

const isValidClientSpecEntry = (entry: ClientSpecMapEntry): entry is EditorClientSpecMapEntry => {
    return 'appDefinitionId' in entry && 'applicationId' in entry && 'instanceId' in entry
}

export const getSettleEndpoint = (): string => editorServerFacade.ENDPOINTS.SETTLE_AND_CONDUCT_V2

export const isDataEmpty = (data: AppsStateDelta): boolean => {
    return _.isEmpty(data.newlyRemovedApps) && _.isEmpty(data.newlyAddedApps)
}

export const getProvisionActionsOfUniqueApps = (clientSpecMap: ClientSpecMap, apps: App[]): AppAction[] => {
    return apps.map(app =>
        createProvisionAction(app.appDefinitionId, app.version, app.sourceTemplateId, app.overrideId)
    )
}

const createProvisionAction = (
    appDefinitionId: string,
    version: string | undefined,
    sourceTemplateId?: string,
    overrideId?: string
): AppAction => {
    const action: AppAction = {
        appDefId: appDefinitionId,
        version,
        ...(overrideId ? {devVersion: {overrideId}} : {})
    }

    if (sourceTemplateId) {
        action.legacyParams = {sourceTemplateId}
    }

    return action
}

const createAddAction = (appDefinitionId: string, version: string): AppAction => {
    return {
        appDefId: appDefinitionId,
        version
    }
}

export const createUpdateAction = (clientSpec: EditorClientSpecMapEntry): AppAction => {
    return {
        appDefId: clientSpec.appDefinitionId,
        version: 'version' in clientSpec ? clientSpec.version : undefined
    }
}

const createRemoveAction = (appDefinitionId: string): AppAction | string => {
    return appDefinitionId
}

const createDismissAction = (appDefId: string): AppAction | string => {
    return appDefId
}

export const getProvisionActionsForAllPages = (
    clientSpecMap: ClientSpecMap,
    semanticAppVersions: Record<string, string> = {}
): AppAction[] => {
    const appsToProvision = installedTpaAppsOnSiteService.getAppsToProvision(clientSpecMap)

    return getProvisionActionsOfUniqueApps(
        clientSpecMap,
        Object.values(appsToProvision).map(({appDefinitionId}) => ({
            appDefinitionId,
            version: semanticAppVersions[appDefinitionId]
        }))
    )
}

export const formatAppsToAddFromAllPages = (
    appsToGrant: App[],
    appsToRevoke: App[],
    clientSpecMap: ClientSpecMap,
    semanticAppVersions: Record<string, string> = {},
    appsToProvision: AppAction[] = []
): AppAction[] => {
    const shouldIncludeApp = ({appDefinitionId}: App): boolean =>
        !appsToRevoke.some(app => app.appDefinitionId === appDefinitionId) &&
        !appsToProvision.some(app => app.appDefId === appDefinitionId)

    const formatApp = ({appDefinitionId, version, appFields}: App): AppAction => {
        const appVersion = appFields?.installedVersion || semanticAppVersions[appDefinitionId] || version
        const action = createAddAction(appDefinitionId, appVersion)
        return _.omitBy(action, _.isUndefined)
    }

    return appsToGrant.filter(shouldIncludeApp).map(formatApp)
}

export const formatAppsToRemove = (appsToRevoke: App[]): (AppAction | string)[] => {
    return appsToRevoke.map(({appDefinitionId}) => createRemoveAction(appDefinitionId))
}

export const formatAppsToDismiss = (): (AppAction | string)[] => {
    const dismissApps = pendingAppsService.getAppsToDismiss()
    return dismissApps.map(appDefId => createDismissAction(appDefId.toString()))
}

export const formatAppsToUpdate = (
    clientSpecMap: ClientSpecMap,
    appsToGrant: App[] = [],
    appsToRevoke: App[] = [],
    appsState: Record<string, {pendingAction?: string; unused?: boolean}> = {}
): AppAction[] => {
    const shouldUpdateApp = (entry: ClientSpecMapEntry): boolean => {
        if (!isValidClientSpecEntry(entry)) return false

        const appState = appsState?.[entry.appDefinitionId] || {}
        const isPendingUpdate = appState.pendingAction === constants.APP_ACTION_TYPES.UPDATE
        const isNotBeingGranted = !appsToGrant.some(app => app.appDefinitionId === entry.appDefinitionId)
        const isNotBeingRevoked = !appsToRevoke.some(app => app.appDefinitionId === entry.appDefinitionId)

        return isPendingUpdate && isNotBeingGranted && isNotBeingRevoked
    }

    return Object.values(clientSpecMap)
        .filter(shouldUpdateApp)
        .map(entry => createUpdateAction(entry as EditorClientSpecMapEntry))
}

export const ensureUniqueAppsStateDelta = (
    newlyAddedApps: AppAction[] = [],
    newlyRemovedApps: string[] = []
): AppsStateDelta => {
    const uniqueAddedApps = _.uniqBy(newlyAddedApps, 'appDefId')
    const uniqueRemovedApps = _.uniq(newlyRemovedApps)

    return {
        newlyAddedApps: uniqueAddedApps,
        newlyRemovedApps: uniqueRemovedApps
    }
}
